// One-to-one WebRTC call using the "perfect negotiation" pattern
// (https://w3c.github.io/webrtc-pc/#perfect-negotiation-example).
// The guest is the polite peer. Media is DTLS-SRTP end-to-end; the
// signaling channel carries only SDP and ICE candidates.
import type { Signaling } from "./signaling"
import type { SignalData } from "./types"

type Options = {
  signaling: Signaling
  polite: boolean
  stream: MediaStream
  iceServers: RTCIceServer[]
  onTrack: (stream: MediaStream) => void
  onConnectionState?: (state: RTCPeerConnectionState) => void
  onDiagnostics?: (d: CallDiagnostics) => void
}

// What the ICE layer is doing, for the "taking longer than usual" overlay:
// enough to tell "no relay reachable" from "relay fine, peer never answered"
// without a debugger. Nothing here identifies anyone — types, not addresses.
export type CallDiagnostics = {
  ice: RTCIceConnectionState
  gathering: RTCIceGatheringState
  signaling: RTCSignalingState
  // Candidate types this side gathered: host / srflx (STUN) / relay (TURN).
  local: string[]
  remoteCandidates: number
  remoteDescription: boolean
  selectedPair: string | null
}

export class Call {
  private pc: RTCPeerConnection | null = null
  private makingOffer = false
  private ignoreOffer = false
  // Named senders: with screen sharing there can be two video senders at
  // once, so "find the sender by track kind" is no longer unambiguous.
  private audioSender: RTCRtpSender | null = null
  private videoSender: RTCRtpSender | null = null
  private screenSender: RTCRtpSender | null = null
  private localTypes = new Set<string>()
  private remoteCandidates = 0

  constructor(private readonly opts: Options) {}

  private async report(): Promise<void> {
    const pc = this.pc
    if (!pc || !this.opts.onDiagnostics) return
    let selectedPair: string | null = null
    try {
      const stats = await pc.getStats()
      stats.forEach((r) => {
        if (r.type === "candidate-pair" && (r.selected || r.state === "succeeded") && !selectedPair) {
          const l = stats.get(r.localCandidateId)
          const rr = stats.get(r.remoteCandidateId)
          selectedPair = `${l?.candidateType ?? "?"} → ${rr?.candidateType ?? "?"}`
        }
      })
    } catch {
      /* stats unavailable */
    }
    this.opts.onDiagnostics({
      ice: pc.iceConnectionState,
      gathering: pc.iceGatheringState,
      signaling: pc.signalingState,
      local: [...this.localTypes],
      remoteCandidates: this.remoteCandidates,
      remoteDescription: !!pc.remoteDescription,
      selectedPair,
    })
  }

  start(): void {
    const pc = new RTCPeerConnection({ iceServers: this.opts.iceServers })
    this.pc = pc

    for (const track of this.opts.stream.getTracks()) {
      const sender = pc.addTrack(track, this.opts.stream)
      if (track.kind === "video") this.videoSender = sender
      else this.audioSender = sender
    }

    pc.ontrack = ({ streams }) => {
      if (streams[0]) this.opts.onTrack(streams[0])
    }
    pc.onicecandidate = ({ candidate }) => {
      if (candidate?.type) this.localTypes.add(candidate.type)
      this.opts.signaling.send({ t: "signal", data: { candidate: candidate ? candidate.toJSON() : null } })
      void this.report()
    }
    pc.oniceconnectionstatechange = () => void this.report()
    pc.onicegatheringstatechange = () => void this.report()
    pc.onsignalingstatechange = () => void this.report()
    pc.onnegotiationneeded = async () => {
      try {
        this.makingOffer = true
        await pc.setLocalDescription()
        this.opts.signaling.send({ t: "signal", data: { description: pc.localDescription ?? undefined } })
      } catch (err) {
        console.error("negotiation", err)
      } finally {
        this.makingOffer = false
      }
    }
    pc.onconnectionstatechange = () => {
      this.opts.onConnectionState?.(pc.connectionState)
      if (pc.connectionState === "failed") pc.restartIce()
    }
  }

  // Handle a relayed {description|candidate} from the other side.
  async handleSignal({ description, candidate }: SignalData): Promise<void> {
    const pc = this.pc
    if (!pc) return
    try {
      if (description) {
        const collision =
          description.type === "offer" && (this.makingOffer || pc.signalingState !== "stable")
        this.ignoreOffer = !this.opts.polite && collision
        if (this.ignoreOffer) return
        await pc.setRemoteDescription(description)
        if (description.type === "offer") {
          await pc.setLocalDescription()
          this.opts.signaling.send({ t: "signal", data: { description: pc.localDescription ?? undefined } })
        }
      } else if (candidate !== undefined) {
        if (candidate) this.remoteCandidates += 1
        try {
          await pc.addIceCandidate(candidate ?? undefined)
        } catch (err) {
          if (!this.ignoreOffer) throw err
        }
      }
    } catch (err) {
      console.error("signal handling", err)
    }
  }

  // Swap what the camera-path senders carry (device switch, blur on/off).
  // The screen sender is deliberately untouched by this.
  async replaceTrack(kind: "audio" | "video", track: MediaStreamTrack | null): Promise<void> {
    const sender = kind === "video" ? this.videoSender : this.audioSender
    if (sender) await sender.replaceTrack(track)
  }

  // Send the screen capture as a second video track alongside the camera.
  // addTrack/removeTrack fire onnegotiationneeded, and the perfect
  // negotiation handlers above take care of the re-offer.
  addScreenTrack(track: MediaStreamTrack, stream: MediaStream): void {
    if (!this.pc || this.screenSender) return
    this.screenSender = this.pc.addTrack(track, stream)
  }

  removeScreenTrack(): void {
    if (!this.pc || !this.screenSender) return
    try {
      this.pc.removeTrack(this.screenSender)
    } catch {
      /* connection already closed */
    }
    this.screenSender = null
  }

  restart(): void {
    this.pc?.restartIce()
  }

  close(): void {
    try {
      this.pc?.close()
    } catch {
      /* already closed */
    }
    this.pc = null
    this.audioSender = null
    this.videoSender = null
    this.screenSender = null
  }
}
